import { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Box,
  Button,
  Alert,
} from "@mui/material";

import { FaEdit, FaTrash } from "react-icons/fa";

import AddEmployeeDialog from "./AddEmployeeDialog";
import EditEmployeeDialog from "./EditEmployeeDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import axiosInstance from "../../axiosInstance";
import { getLoginUserRoleId } from "../../_lib";
import Stack from "@mui/material/Stack";

function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const loginUserRoleId = getLoginUserRoleId();

  // --------------------------------------------
  // Load employees from backend
  // --------------------------------------------
  const fetchEmployees = async () => {
    try {
      const res = await axiosInstance.get("/employees");
      setEmployees(res.data);
    } catch (err) {
      setError("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // --------------------------------------------
  // Add employee to list (after backend creation)
  // --------------------------------------------
  const handleAddEmployee = (newEmp) => {
    fetchEmployees();
  };

  // --------------------------------------------
  // Update employee in list (after edit)
  // --------------------------------------------
  const handleUpdateEmployee = (updatedEmp) => {
    fetchEmployees();
  };

  // --------------------------------------------
  // Delete employee from backend + list
  // --------------------------------------------
  const handleDeleteEmployee = async () => {
    try {
      await axiosInstance.delete(`/employees/${selectedEmployee.userId}`);

      setEmployees(
        employees.filter((e) => e.userId !== selectedEmployee.userId)
      );

      setOpenDelete(false);
    } catch (err) {
      setError("Failed to delete employee");
    }
  };

  // --------------------------------------------
  // Loading spinner
  // --------------------------------------------
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={3}
      >
        <Typography variant="h5" fontWeight="bold">
          Employees
        </Typography>

        {/* Add Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={() => setOpenAdd(true)}>
            Add Employee
          </Button>
        </Box>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}

      {/* Dialogs */}
      <AddEmployeeDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSave={handleAddEmployee}
      />

      <EditEmployeeDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        employee={selectedEmployee}
        onUpdate={handleUpdateEmployee}
      />

      <ConfirmDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDeleteEmployee}
        message={`Are you sure you want to delete ${selectedEmployee?.fullName}?`}
      />

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f4f4f4" }}>
              <TableCell>
                <strong>User ID</strong>
              </TableCell>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>Mobile</strong>
              </TableCell>
              {loginUserRoleId !== 2 && loginUserRoleId !== 3 && (
                <TableCell>
                  <strong>Employer</strong>
                </TableCell>
              )}
              <TableCell>
                <strong>Role</strong>
              </TableCell>
              <TableCell>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {employees.map((emp) => (
              <TableRow key={emp.userId}>
                <TableCell>{emp.userId}</TableCell>
                <TableCell>{emp.fullName}</TableCell>
                <TableCell>{emp.mobile}</TableCell>
                {loginUserRoleId !== 2 && loginUserRoleId !== 3 && (
                  <TableCell>{emp.employerDetails?.employerName}</TableCell>
                )}
                <TableCell>{emp.roleName}</TableCell>

                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setSelectedEmployee(emp);
                      setOpenEdit(true);
                    }}
                  >
                    <FaEdit size={19} />
                  </IconButton>

                  <IconButton
                    color="error"
                    onClick={() => {
                      setSelectedEmployee(emp);
                      setOpenDelete(true);
                    }}
                  >
                    <FaTrash size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No employees found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default EmployeeList;